import {Component, effect, inject, OnInit} from '@angular/core';
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {TranslateModule} from "@ngx-translate/core";
import {JobStatusItem} from "../../core/definitions/job-status-item";
import {LoginService} from "../../core/login.service";
import {UserData} from "../../core/definitions/user-data";
import {UserPrivilege} from "../../administration/admin-user/User";
import {JobStatusSubscriberService} from "../../core/jobstatussubcriber/job-status-subscriber.service";
import {JobStatusItemComponent} from "./job-status-item/job-status-item.component";
import { OperationService } from '../../operations/operation.service';
import { FinishedJobStepsService } from '../../operations/finished-job-steps.service';
import { OperationStep } from '../../core/definitions/operation-step';
import {CurrentObjectContext} from "../../object-page-v2/current-object.context";

@Component({
    selector: 'app-job-status-v2',
    imports: [
        MatTabGroup,
        MatTab,
        TranslateModule,
        JobStatusItemComponent
    ],
    templateUrl: './job-status-v2.component.html',
    styleUrl: './job-status-v2.component.scss'
})
export class JobStatusV2Component implements OnInit {
  // Injections
  private readonly currentObjectContext: CurrentObjectContext = inject(CurrentObjectContext);
  private readonly jobStatusService: JobStatusSubscriberService = inject(JobStatusSubscriberService);
  private readonly loginService: LoginService = inject(LoginService);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly finishedJobSteps: FinishedJobStepsService = inject(FinishedJobStepsService);

  private currentUser: UserData = null;
  private lastRefreshingJobId = null;

  constructor() {
    effect(() => {
      if (this.jobStatusService.allJobs().length) {
        for (const job of this.jobStatusService.allJobs()) {
          this.getStepSetJobStatusItemRef(job);
        }

        let res = null;
        let lastEndedDate = null;

        for (const job of this.userJobs) {
          if (job.endedDate) {
            const endedDate = job.endedDate;

            if (!lastEndedDate || endedDate > lastEndedDate) {
              res = job;
              lastEndedDate = endedDate;
            }
          }
        }

        if (res && res.result_object?.object_refresh && res.queue_id !== this.lastRefreshingJobId) {
          this.currentObjectContext.objectRefresh({});
          this.lastRefreshingJobId = res.queue_id;
        }
      }
    })
  }

  ngOnInit () {
    this.loginService.currentUser.subscribe(user => {
      this.currentUser = user;
    });
  }

  get allJobs(): JobStatusItem[] {
    return this.jobStatusService.allJobs();
  }

  get userJobs(): JobStatusItem[] {
    return this.jobStatusService.allJobs().filter(job => job.created_by_id.replace('USER-', '') === this.currentUser?.artifact_id.replace('USER-', ''));
  }

  get userIsAdmin(): boolean {
    return this.currentUser?.rights_level === UserPrivilege.ADMIN;
  }

  /**
   * Sets a finished job's link from its next step, which `FinishedJobStepsService` asks
   * the backend for once per job; an answer already kept is used at once.
   */
  private getStepSetJobStatusItemRef(jobStatusItem: JobStatusItem) {
    if (jobStatusItem.status === 'step complete') {
      const queueId = jobStatusItem.queue_id;
      if (this.finishedJobSteps.has(queueId)) {
        this.setJobStatusItemRef(this.finishedJobSteps.get(queueId), jobStatusItem);
        return;
      }
      this.finishedJobSteps.nextStep(queueId)
        .then(step => this.setJobStatusItemRef(step, jobStatusItem))
        .catch(() => undefined);
    }
  }

  private setJobStatusItemRef(step: OperationStep, jobStatusItem: JobStatusItem) {
    if (step && step.change_state) {
      [jobStatusItem.ref, jobStatusItem.refParams] = this.operationService.getStateParams(
        step.change_state[0], jobStatusItem);
    }
  }
}
